// AccountUpgrade — the ONE credentials surface ("one account, one
// upgrade"). Client accounts are phone-verified passwordless; this is
// the single place a client adds an email + password so they can sign
// in anywhere. Mounted at every door: post-booking confirmation,
// settings (ChangePasswordCard when no password yet), the account
// bottom sheet, and the Login sign-in ratchet.
//
// Two render variants:
//   variant="sheet" — house BottomSheet chrome; props { open, onClose }
//   variant="card"  — inline card body for settings embedding
//
// `context` tunes one intro line ('post-booking' | 'settings' |
// 'signin-ratchet'); 'settings' adds none.
//
// Wire contract (server): POST /api/auth/set-credentials
//   body { email?, password } → 200 { user } (public-profile shape,
//   passed to setUser). 400 password < 8; 409 already has password /
//   email in use — both with { message }.
//
// CredentialsFields is exported so ClaimAccount renders the identical
// email/password/confirm trio (its own submit hits the claim-token
// endpoint instead).

import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import {
  AlertCircle, ArrowRight, Check, Eye, EyeOff, Globe, Loader2, Lock,
  Package, Receipt,
} from 'lucide-react';
import { AuthContext } from '../AuthContext';
import BottomSheet from './ui/BottomSheet';

// Same field chrome as Login.js so the credentials ask reads as one
// surface everywhere it appears.
const inputClass = `block w-full px-3.5 py-3.5 bg-paper-elev border border-line rounded-btn
  text-[15px] text-ink placeholder-ink-3
  focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent
  transition-all`;

const CONTEXT_INTRO = {
  'post-booking': 'Your booking is confirmed — save your account for next time.',
  'signin-ratchet': 'Before you continue — make your account portable.',
  // 'settings' deliberately has no intro line.
};

/* Shared email/password/confirm trio. Controlled — the parent owns the
   values and the submit. Email renders only when the account has none
   yet (`needsEmail`). Min-8 + mismatch copy live here so validation
   reads identically on every door. */
export function CredentialsFields({
  needsEmail,
  email, onEmailChange,
  password, onPasswordChange,
  confirm, onConfirmChange,
}) {
  const [showPassword, setShowPassword] = useState(false);
  const mismatch = confirm.length > 0 && password !== confirm;
  return (
    <>
      {needsEmail && (
        <div>
          <label htmlFor="au-email" className="av-meta block mb-1.5">Email</label>
          <input
            id="au-email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            placeholder="you@example.com"
            className={inputClass}
          />
          <p className="mt-1 text-xs text-ink-3">You'll use this to sign in.</p>
        </div>
      )}
      <div>
        <label htmlFor="au-password" className="av-meta block mb-1.5">Password</label>
        <div className="relative">
          <input
            id="au-password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            required
            minLength={8}
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            placeholder="At least 8 characters"
            className={`${inputClass} pr-12`}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="absolute inset-y-0 right-0 pr-4 flex items-center text-ink-3 hover:text-ink-2 transition"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <div>
        <label htmlFor="au-confirm" className="av-meta block mb-1.5">Confirm password</label>
        <input
          id="au-confirm"
          type={showPassword ? 'text' : 'password'}
          autoComplete="new-password"
          required
          value={confirm}
          onChange={(e) => onConfirmChange(e.target.value)}
          placeholder="Type it again"
          className={inputClass}
        />
        {mismatch && (
          <p className="mt-1 text-xs text-[var(--r-off-ink)]">Passwords don't match.</p>
        )}
      </div>
    </>
  );
}

const VALUE_LINES = [
  { icon: Receipt, text: 'Receipts & rebooking in one place' },
  { icon: Package, text: 'Packages & memberships, tracked for you' },
  { icon: Globe, text: 'Sign in anywhere — any device, any provider' },
];

export default function AccountUpgrade({
  variant = 'sheet',
  open = false,
  onClose,
  context,
  title = 'Add email & password',
  // Called from the Done button on the success state (receives the
  // updated user). Falls back to onClose when absent.
  onDone,
  // Login's ratchet can't put the verified user into AuthContext yet
  // (the /login route redirects the moment context user is set), so it
  // hands the user in directly for the email gating.
  userOverride = null,
}) {
  const auth = useContext(AuthContext);
  const user = userOverride || auth?.user || null;
  const setUser = auth?.setUser;
  const needsEmail = !user?.email;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [savedUser, setSavedUser] = useState(null);

  // Sheet reuse: a fresh form each time it opens.
  useEffect(() => {
    if (variant !== 'sheet' || !open) return;
    setEmail(''); setPassword(''); setConfirm('');
    setBusy(false); setError(''); setDone(false); setSavedUser(null);
  }, [variant, open]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    if (needsEmail && !email.trim()) { setError('Enter the email you want to sign in with.'); return; }
    setBusy(true);
    try {
      const body = { password };
      if (needsEmail) body.email = email.trim();
      const res = await axios.post('/api/auth/set-credentials', body, { withCredentials: true });
      const updated = res.data?.user || null;
      setSavedUser(updated);
      // Context safety (review blocker, 2026-08-27): the response is a
      // BARE public profile — no provider/joinCode decoration — so it
      // must MERGE into the auth context, never replace it (the
      // spread-clobber footgun: a wholesale setUser stranded upgraded
      // clients on ProviderPageUnavailable). With a userOverride the
      // caller owns context entirely (Login's ratchet must not setUser
      // before its pane finishes — /login redirects the moment user is
      // set); onDone receives the saved profile to merge itself.
      if (updated && !userOverride && typeof setUser === 'function') {
        setUser(prev => (prev ? { ...prev, ...updated } : updated));
      }
      setDone(true);
    } catch (err) {
      // 400 (password < 8) and 409 (already has password / email in
      // use) both carry { message }.
      setError(err?.response?.data?.message || 'Could not save your credentials. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleDone = () => {
    if (onDone) onDone(savedUser);
    else if (onClose) onClose();
  };

  const submitDisabled = busy
    || password.length < 8
    || password !== confirm
    || (needsEmail && !email.trim());

  const body = done ? (
    <div className="flex flex-col gap-4">
      <div
        className="rounded-lg p-4"
        style={{
          backgroundColor: 'var(--accent-soft)',
          border: '1px solid var(--accent-glow, var(--line))',
        }}
      >
        <div
          className="text-sm inline-flex items-center gap-2"
          style={{ color: 'var(--accent-ink)', fontWeight: 600 }}
        >
          <Check className="w-4 h-4" />
          You're set.
        </div>
        <div className="text-xs mt-1.5" style={{ color: 'var(--ink-2)', lineHeight: 1.5 }}>
          You can now sign in with your email anywhere.
        </div>
      </div>
      <button
        type="button"
        onClick={handleDone}
        className="w-full inline-flex items-center justify-center gap-2 py-3.5 px-5
          rounded-btn text-[15px] font-medium text-white bg-accent
          hover:bg-accent-ink transition"
      >
        Done
      </button>
    </div>
  ) : (
    <form onSubmit={submit} className="flex flex-col gap-3.5">
      {CONTEXT_INTRO[context] && (
        <p className="text-sm text-ink-2 m-0" style={{ lineHeight: 1.5 }}>
          {CONTEXT_INTRO[context]}
        </p>
      )}
      <div className="rounded-lg p-4 border border-line bg-paper">
        <div className="text-base font-medium text-ink" style={{ letterSpacing: '-0.005em' }}>
          One place for everything
        </div>
        <ul className="mt-2 space-y-1.5" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {VALUE_LINES.map(({ icon: Icon, text }) => (
            <li key={text} className="flex items-start gap-2 text-xs" style={{ color: 'var(--ink-2)', lineHeight: 1.5 }}>
              <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--accent-ink)', marginTop: 1 }} />
              {text}
            </li>
          ))}
        </ul>
      </div>
      <CredentialsFields
        needsEmail={needsEmail}
        email={email}
        onEmailChange={setEmail}
        password={password}
        onPasswordChange={setPassword}
        confirm={confirm}
        onConfirmChange={setConfirm}
      />
      {error && (
        <div className="flex items-start gap-2 p-3 border border-[rgba(192,106,62,0.30)] bg-[var(--r-off-soft)] rounded-btn">
          <AlertCircle className="h-5 w-5 text-[var(--r-off)] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-[var(--r-off-ink)]">{error}</p>
        </div>
      )}
      <button
        type="submit"
        disabled={submitDisabled}
        className="w-full inline-flex items-center justify-center gap-2 py-3.5 px-5
          rounded-btn text-[15px] font-medium text-white bg-accent
          hover:bg-accent-ink transition
          disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {busy ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
        ) : (
          <>Save email &amp; password <ArrowRight className="w-4 h-4" /></>
        )}
      </button>
    </form>
  );

  if (variant === 'card') {
    return (
      <div className="rounded-card border border-line bg-paper-elev px-5 py-5 max-w-3xl">
        <div className="flex items-center gap-2 mb-3">
          <Lock className="w-4 h-4 text-accent" />
          <h3 className="font-medium text-ink text-base">{title}</h3>
        </div>
        {body}
      </div>
    );
  }

  return (
    <BottomSheet open={open} onClose={onClose} eyebrow="Your account" title={title} zIndex={70}>
      {body}
    </BottomSheet>
  );
}
